import React, { memo, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import type { ScanParticipant } from '../types/types';

type CardTone = 'blue' | 'orange';

type ParticipantDetailCardProps = {
	participant: ScanParticipant;
	tone: CardTone;
	footer?: ReactNode;
};

// 스캔 참가자 상세 카드 (체크인 확인 카드)
const ParticipantDetailCard = memo<ParticipantDetailCardProps>(({ participant, tone, footer }) => {
	return (
		<Wrapper $tone={tone}>
			<Content>
				<LeftColumn>
					<Field>
						<FieldLabel>Bib No.</FieldLabel>
						<BibValue $tone={tone}>{participant.bibNumber}</BibValue>
					</Field>
					<Divider />
					<OptionRow>
						<Field>
							<FieldLabel>대회 선택 옵션</FieldLabel>
							<StrongValue $tone={tone}>{participant.sizeOption}</StrongValue>
						</Field>
						<Field>
							<FieldLabel>코스</FieldLabel>
							<StrongValue $tone={tone}>{participant.courseName}</StrongValue>
						</Field>
					</OptionRow>
				</LeftColumn>
				<RightColumn>
					<Field>
						<FieldLabel>이름</FieldLabel>
						<NameValue $tone={tone}>
							{participant.name} {participant.englishName}
						</NameValue>
					</Field>
					<InfoGrid>
						<Field>
							<FieldLabel>국적</FieldLabel>
							<StrongValue $tone={tone}>{participant.nationality}</StrongValue>
						</Field>
						<Field>
							<FieldLabel>생년월일</FieldLabel>
							<StrongValue $tone={tone}>{participant.birthDate}</StrongValue>
						</Field>
						<Field>
							<FieldLabel>신청구분</FieldLabel>
							<StrongValue $tone={tone}>{participant.applicationType}</StrongValue>
						</Field>
						<Field>
							<FieldLabel>비상 연락처</FieldLabel>
							<StrongValue $tone={tone}>{participant.emergencyContact}</StrongValue>
						</Field>
					</InfoGrid>
				</RightColumn>
			</Content>
			{footer && <Footer>{footer}</Footer>}
		</Wrapper>
	);
});

ParticipantDetailCard.displayName = 'ParticipantDetailCard';

export default ParticipantDetailCard;

const toneColor = (tone: CardTone, theme: any) =>
	tone === 'blue' ? theme.colors.activeBlue : theme.colors.activeRedOrange;

const Wrapper = styled.div<{ $tone: CardTone }>`
	padding: 32px;
	border-radius: 8px;
	background-color: ${({ $tone }) => ($tone === 'blue' ? '#EAF3FF' : '#FFEDE3')};
`;
const Content = styled.div`
	display: flex;
	gap: 40px;
`;
const LeftColumn = styled.div`
	width: 320px;
	flex-shrink: 0;
`;
const RightColumn = styled.div`
	flex: 1;
	min-width: 0;
`;
const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;
const FieldLabel = styled.div`
	font-size: 14px;
	color: ${({ theme }) => theme.colors.deepGrayFontColor};
`;
const Divider = styled.div`
	height: 1px;
	margin: 28px 0;
	background-color: rgba(0, 0, 0, 0.08);
`;
const OptionRow = styled.div`
	display: flex;
	gap: 60px;
`;
const InfoGrid = styled.div`
	display: grid;
	grid-template-columns: 140px 1fr;
	gap: 24px 20px;
	margin-top: 28px;
`;
const BibValue = styled.div<{ $tone: CardTone }>`
	font-size: 48px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	color: ${({ $tone, theme }) => toneColor($tone, theme)};
`;
const NameValue = styled.div<{ $tone: CardTone }>`
	font-size: 28px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	color: ${({ $tone, theme }) => toneColor($tone, theme)};
`;
const StrongValue = styled.div<{ $tone: CardTone }>`
	font-size: 20px;
	font-family: PretendardMedium, AppleSDGothicNeoMedium, sans-serif;
	color: ${({ $tone, theme }) => toneColor($tone, theme)};
`;
const Footer = styled.div`
	display: flex;
	gap: 16px;
	margin-top: 32px;
`;
